/**
 * État client des notifications utilisateur : compteur de non-lues (cloche), liste paginée
 * (modale) et pastilles « non lu » des tickets. Rafraîchi par l'événement temps réel
 * `notification` ({ phase: "created" | "invalidated" }) : livré uniquement à l'utilisateur ciblé : * et par un bus local (`notifyNotificationsChanged`) après un marquage « lu », qui n'émet aucun
 * événement serveur.
 */
import { useCallback, useEffect, useState } from "react";
import { useRealtimeEvent } from "./RealtimeProvider";
import { notificationsClient, type AdminNotification } from "../Pages/Notifications/notificationsClient";

export const NOTIFICATIONS_PAGE_SIZE = 20;

/** Payload de l'événement hub `notification`. */
export type NotificationRealtimePayload =
	| { phase: "created"; id: string; topic: string; title: string; linkPath: string }
	| { phase: "read"; sourceKeyPrefix: string; count: number }
	| { phase: "invalidated"; sourceKey?: string; sourceKeyPrefix?: string; count?: number };

// ---------------------------------------------------------------------------
// Bus local : « quelque chose a changé côté notifications » (ex. markRead) : le serveur n'émet
// pas d'événement pour une lecture, donc la cloche et les pastilles s'abonnent ici aussi.
// ---------------------------------------------------------------------------
const changeListeners = new Set<() => void>();

/** Préviens tous les abonnés locaux (cloche, pastilles tickets) qu'un état lu/non-lu a changé. */
export function notifyNotificationsChanged(): void {
	changeListeners.forEach((listener) => {
		try {
			listener();
		} catch {
			/* un abonné cassé ne doit pas bloquer les autres */
		}
	});
}

function useNotificationsChanged(handler: () => void): void {
	useEffect(() => {
		changeListeners.add(handler);
		return () => {
			changeListeners.delete(handler);
		};
	}, [handler]);
}

/** Abonnement PUBLIC au bus local (cloche, pastilles, badge latéral des tickets). */
export function useNotificationsChangedListener(handler: () => void): void {
	useNotificationsChanged(handler);
}

// ---------------------------------------------------------------------------
// Compteur de non-lues (badge de la cloche)
// ---------------------------------------------------------------------------

/** Compteur léger de notifications non lues : un fetch au montage, puis rafraîchi à chaque
 *  événement temps réel `notification` et à chaque changement local (markRead). */
export function useNotificationUnreadCount(): number {
	const [count, setCount] = useState(0);

	const refresh = useCallback(() => {
		notificationsClient
			.unreadCount()
			.then((n) => setCount(Number.isFinite(n) ? n : 0))
			.catch(() => {
				/* on garde le dernier compteur connu sur une erreur transitoire */
			});
	}, []);

	useEffect(() => {
		refresh();
	}, [refresh]);

	useRealtimeEvent("notification", () => refresh());
	useNotificationsChanged(refresh);

	return count;
}

// ---------------------------------------------------------------------------
// Liste paginée (modale)
// ---------------------------------------------------------------------------

export interface NotificationsListState {
	items: AdminNotification[];
	loading: boolean;
	page: number;
	/** Vrai quand la page suivante existe probablement (page pleine). */
	hasMore: boolean;
	setPage: (page: number) => void;
	reload: () => void;
	/** Marque lue côté serveur, met la ligne à jour localement et prévient la cloche/les pastilles. */
	markRead: (id: string) => Promise<void>;
}

/**
 * Liste paginée des notifications de l'utilisateur (skip/take côté serveur). Les lignes
 * `invalidated` sont écartées. Sur un événement temps réel : `created` recharge la première page
 * (la ligne fraîche arrive en tête), `invalidated` recharge la page courante.
 */
export function useNotifications(open: boolean): NotificationsListState {
	const [page, setPage] = useState(1);
	const [items, setItems] = useState<AdminNotification[]>([]);
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(false);

	// Retour à la première page à chaque (ré)ouverture.
	useEffect(() => {
		if (open) setPage(1);
	}, [open]);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const rows = await notificationsClient.list((page - 1) * NOTIFICATIONS_PAGE_SIZE, NOTIFICATIONS_PAGE_SIZE);
			setItems(rows.filter((n) => n.status !== "invalidated"));
			setHasMore(rows.length === NOTIFICATIONS_PAGE_SIZE);
		} catch {
			setItems([]);
			setHasMore(false);
		} finally {
			setLoading(false);
		}
	}, [page]);

	useEffect(() => {
		if (open) void load();
	}, [open, load]);

	useRealtimeEvent("notification", (payload: NotificationRealtimePayload) => {
		if (!open) return;
		if (payload?.phase === "created") {
			if (page === 1) void load();
			else setPage(1); // le changement de page déclenche le rechargement
		} else {
			void load();
		}
	});

	const markRead = useCallback(
		async (id: string) => {
			try {
				await notificationsClient.markRead(id);
				setItems((current) => current.map((n) => (n.id === id ? { ...n, status: "read" as const } : n)));
				notifyNotificationsChanged();
			} catch {
				/* la navigation reste possible même si le marquage échoue */
			}
		},
		[]
	);

	return { items, loading, page, hasMore, setPage, reload: load, markRead };
}

// ---------------------------------------------------------------------------
// Pastilles « non lu » des tickets : cache module partagé entre toutes les cartes de la liste
// (une seule requête pour N cartes, dédupliquée par promesse en vol).
// ---------------------------------------------------------------------------

/** Le sujet des notifications de tickets. */
export const TICKETS_TOPIC = "tickets";
const TICKET_SCAN_TAKE = 200;

let ticketUnread: AdminNotification[] = [];
let ticketUnreadLoaded = false;
let ticketInflight: Promise<void> | null = null;
const ticketListeners = new Set<() => void>();

function refreshTicketUnread(force: boolean): Promise<void> {
	if (ticketInflight) return ticketInflight;
	if (ticketUnreadLoaded && !force) return Promise.resolve();
	ticketInflight = notificationsClient
		.list(0, TICKET_SCAN_TAKE)
		.then((rows) => {
			ticketUnread = rows.filter((n) => n.topic === TICKETS_TOPIC && n.status === "unread");
			ticketUnreadLoaded = true;
			ticketListeners.forEach((listener) => {
				try {
					listener();
				} catch {
					/* idem : un abonné cassé n'arrête pas la diffusion */
				}
			});
		})
		.catch(() => {
			/* on garde le dernier état connu */
		})
		.finally(() => {
			ticketInflight = null;
		});
	return ticketInflight;
}

/** Réinitialise le cache module : réservé aux tests. */
export function __resetTicketUnreadCacheForTests(): void {
	ticketUnread = [];
	ticketUnreadLoaded = false;
	ticketInflight = null;
}

/** Vrai quand une notification de ticket NON LUE pointe vers ce ticket (linkPath « ?ticket={id} »). */
export function useTicketNotificationUnread(ticketId: string): boolean {
	const [, setVersion] = useState(0);
	const bump = useCallback(() => setVersion((v) => v + 1), []);

	useEffect(() => {
		ticketListeners.add(bump);
		void refreshTicketUnread(false);
		return () => {
			ticketListeners.delete(bump);
		};
	}, [bump]);

	// Un événement notification (création/invalidation) ou un markRead local peut changer la pastille.
	useRealtimeEvent("notification", () => void refreshTicketUnread(true));
	useNotificationsChanged(useCallback(() => void refreshTicketUnread(true), []));

	if (!ticketId) return false;
	const needle = `ticket=${ticketId}`;
	const encoded = `ticket=${encodeURIComponent(ticketId)}`;
	return ticketUnread.some((n) => n.linkPath.includes(needle) || n.linkPath.includes(encoded));
}
